import styled, { css, DefaultTheme } from 'styled-components';
import { Field as UnstyledField } from 'formik';
import CurrencyField from 'react-currency-format';
import InputMask from 'react-input-mask';

type LabelProps = {
  required?: boolean;
};

type InputProps = {
  $isValid?: boolean;
  $prefixWidth?: number;
  $rightContentWidth?: number;
  variant: 'primary' | 'secondary';
  disabled: boolean;
};

const inputModifiers = {
  prefix: (prefixWidth: number) => css`
    padding-left: calc(${prefixWidth}px + 24px); ;
  `,
  rightContent: (contentWidth: number) => css`
    padding-right: calc(${contentWidth}px + 16px);
  `,
  invalid: (theme: DefaultTheme) => css`
    outline-color: ${theme.colors.statusDanger20};
    border-color: ${theme.colors.statusDanger20};
  `,
};

export const Container = styled.div`
  position: relative;
`;

export const Prefix = styled.div`
  ${({ theme }) => css`
    font-size: 1.8rem;
    line-height: 2.8rem;
    color: ${theme.colors.neutralSilver40};

    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(calc(-50% - 2px));
  `}
`;

export const RightContent = styled.div`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
`;

const inputStyle = (
  theme: DefaultTheme,
  variant: 'primary' | 'secondary',
  disabled: boolean
) => css`
  width: 100%;

  font-size: 1.6rem;
  line-height: 2.6rem;
  font-weight: ${variant === 'primary' ? 400 : 600};
  color: ${variant === 'primary'
    ? theme.colors.neutralSilver80
    : theme.colors.neutralSilver100};

  outline-color: ${theme.colors.neutralSilver10};
  border: 1px solid ${theme.colors.neutralSilver10};
  border-radius: 4px;
  padding: 14px 16px;

  &::placeholder {
    color: ${theme.colors.neutralSilver50};
    font-weight: 400;
  }

  &:hover {
    border: 1px solid ${theme.colors.purple50};
  }

  &:active,
  &:focus {
    border: 1px solid ${theme.colors.brandPrimary100};
    outline: 1px solid ${theme.colors.brandPrimary30};
    outline-offset: 1px;
  }

  &:disabled {
    background-color: ${theme.colors.neutralSilver10};
    border: 1px solid ${theme.colors.neutralSilver10};
    cursor: not-allowed;
  }
`;

export const Input = styled(UnstyledField)<InputProps>`
  ${({
    theme,
    variant,
    $isValid = true,
    $prefixWidth,
    $rightContentWidth,
    disabled,
  }) => css`
    ${inputStyle(theme, variant, disabled)};

    ${$prefixWidth && inputModifiers.prefix($prefixWidth)};
    ${$rightContentWidth && inputModifiers.rightContent($rightContentWidth)};
    ${!$isValid && inputModifiers.invalid(theme)}
  `}
`;

export const Currency = styled(CurrencyField)<InputProps>`
  ${({
    theme,
    $isValid = true,
    variant,
    $prefixWidth,
    $rightContentWidth,
    disabled,
  }) => css`
    ${inputStyle(theme, variant, disabled)};

    ${$prefixWidth && inputModifiers.prefix($prefixWidth)};
    ${$rightContentWidth && inputModifiers.rightContent($rightContentWidth)};
    ${!$isValid && inputModifiers.invalid(theme)}
  `}
`;

export const Mask = styled(InputMask)<InputProps>`
  ${({
    theme,
    $isValid = true,
    variant,
    $prefixWidth,
    $rightContentWidth,
    disabled,
  }) => css`
    ${inputStyle(theme, variant, disabled)};

    ${$prefixWidth && inputModifiers.prefix($prefixWidth)};
    ${$rightContentWidth && inputModifiers.rightContent($rightContentWidth)};
    ${!$isValid && inputModifiers.invalid(theme)}
  `}
`;

const labelModifiers = {
  required: (theme: DefaultTheme) => css`
    &::after {
      content: '*';
      margin-left: 8px;
      vertical-align: middle;
      color: ${theme.colors.statusDanger30};
    }
  `,
};

export const Label = styled.label<LabelProps>`
  ${({ theme, required }) => css`
    font-weight: 500;
    font-size: 1.4rem;
    line-height: 2rem;
    color: ${theme.colors.neutralSilver70};

    display: block;

    ${required && labelModifiers.required(theme)}
  `};
`;

export const LabelContainer = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  position: relative;
  z-index: 1;
`;

export const IconWrapper = styled.div`
  @media (max-width: 992px) {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 48px;
    width: 48px;
    position: absolute;
    right: -40px;
    z-index: 2;
  }
`;
